import { keepPreviousData } from "@tanstack/react-query";
import {
  GetSubscriptions,
  GetSubscription,
  PostSubscription,
  PutSubscription,
  DeleteSubscription,
  GetSubscriptionTags,
  GetSubscriptionTag,
  PostSubscriptionTag,
  PutSubscriptionTag,
  DeleteSubscriptionTag,
  GetSubscriptionCategories,
  GetSubscriptionCategory,
  PostSubscriptionCategory,
  PutSubscriptionCategory,
  DeleteSubscriptionCategory,
} from "@/server-types";
import { del, get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

/**
 * GET /subscription
 */
export const useGetSubscriptions = (query?: GetSubscriptions["query"]) => {
  return useCustomQuery({
    queryKey: ["getSubscriptions", query],
    queryFn: () => get<GetSubscriptions>("/subscription", query),
    placeholderData: keepPreviousData,
  });
};

/**
 * GET /subscription/:id
 */
export const useGetSubscription = (subscriptionId: number) => {
  return useCustomQuery({
    queryKey: ["subscription", subscriptionId],
    queryFn: () => get<GetSubscription>(`/subscription/${subscriptionId}`),
    enabled: subscriptionId !== -1,
  });
};

/**
 * POST /subscription
 */
export const usePostSubscription = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscription["body"]) =>
      post<PostSubscription>("/subscription", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getSubscriptions"],
      });
    },
  });
};

/**
 * PUT /subscription/:id
 */
export const usePutSubscription = () => {
  return useCustomMutation({
    mutationFn: ({
      subscriptionId,
      body,
    }: {
      subscriptionId: number;
      body: PutSubscription["body"];
    }) => put<PutSubscription>(`/subscription/${subscriptionId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getSubscriptions"],
      });
    },
  });
};

/**
 * DELETE /subscription/:id
 */
export const useDeleteSubscription = () => {
  return useCustomMutation({
    mutationFn: (subscriptionId: number) =>
      del<DeleteSubscription>(`/subscription/${subscriptionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getSubscriptions"],
      });
    },
  });
};

/**
 * GET /subscription/tag
 */
export const useGetSubscriptionTags = () => {
  return useCustomQuery({
    queryKey: ["subscriptionTags"],
    queryFn: () => get<GetSubscriptionTags>("/subscription/tag"),
  });
};

/**
 * GET /subscription/tag/:id
 */
export const useGetSubscriptionTag = (tagId: number) => {
  return useCustomQuery({
    queryKey: ["subscriptionTag", tagId],
    queryFn: () => get<GetSubscriptionTag>(`/subscription/tag/${tagId}`),
    enabled: tagId !== -1,
  });
};

/**
 * POST /subscription/tag
 */
export const usePostSubscriptionTag = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionTag["body"]) =>
      post<PostSubscriptionTag>("/subscription/tag", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionTags"],
      });
    },
  });
};

/**
 * PUT /subscription/tag/:id
 */
export const usePutSubscriptionTag = () => {
  return useCustomMutation({
    mutationFn: ({
      tagId,
      body,
    }: {
      tagId: number;
      body: PutSubscriptionTag["body"];
    }) => put<PutSubscriptionTag>(`/subscription/tag/${tagId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionTags"],
      });
    },
  });
};

/**
 * DELETE /subscription/tag/:id
 */
export const useDeleteSubscriptionTag = () => {
  return useCustomMutation({
    mutationFn: (tagId: number) =>
      del<DeleteSubscriptionTag>(`/subscription/tag/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionTags"],
      });
    },
  });
};

/**
 * GET /subscription/category
 */
export const useGetSubscriptionCategories = () => {
  return useCustomQuery({
    queryKey: ["subscriptionCategories"],
    queryFn: () => get<GetSubscriptionCategories>("/subscription/category"),
  });
};

/**
 * GET /subscription/category/:id
 */
export const useGetSubscriptionCategory = (categoryId: number) => {
  return useCustomQuery({
    queryKey: ["subscriptionCategory", categoryId],
    queryFn: () =>
      get<GetSubscriptionCategory>(`/subscription/category/${categoryId}`),
    enabled: categoryId !== -1,
  });
};

/**
 * POST /subscription/category
 */
export const usePostSubscriptionCategory = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionCategory["body"]) =>
      post<PostSubscriptionCategory>("/subscription/category", body),
  });
};

/**
 * DELETE /subscription/category/:id
 */
export const useDeleteSubscriptionCategory = () => {
  return useCustomMutation({
    mutationFn: (categoryId: number) =>
      del<DeleteSubscriptionCategory>(`/subscription/category/${categoryId}`),
  });
};

/**
 * PUT /subscription/category/:id
 */
export const usePutSubscriptionCategory = () => {
  return useCustomMutation({
    mutationFn: ({
      categoryId,
      body,
    }: {
      categoryId: number;
      body: PutSubscriptionCategory["body"];
    }) =>
      put<PutSubscriptionCategory>(
        `/subscription/category/${categoryId}`,
        body,
      ),
  });
};
